import type { User } from "@/lib/api/user";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { UserRecordHeader } from "./user-record-header";
import { UserRecordTabList } from "./user-record-tab-list";
import type {
  UserRecordSurface,
  UserRecordTab,
  UserRecordTabId,
} from "./user-record-tabs";

const USERS_BACK = { to: "/users", label: "Users" } as const;

interface UserRecordShellProps {
  user: User;
  surface: UserRecordSurface;
  tabs: UserRecordTab[];
  activeTabId: UserRecordTabId;
  onTabSelect: (id: UserRecordTabId) => void;
  badges?: Partial<Record<UserRecordTabId, number>>;
  back?: { to: "/users" | "/care-reviews"; label: string };
  children: ReactNode;
}

export function UserRecordShell({
  user,
  surface,
  tabs,
  activeTabId,
  onTabSelect,
  badges,
  back = USERS_BACK,
  children,
}: UserRecordShellProps) {
  return (
    <div className="min-h-screen bg-surfaces-z2">
      <div className="mx-auto flex max-w-[1320px] flex-col gap-4 px-6 py-6">
        <div className="flex flex-col">
          {surface === "record" ? (
            <div className="flex h-11 items-center">
              <Link
                to={back.to}
                className="inline-flex items-center gap-1.5 font-body text-sm font-medium leading-5 text-text-secondary"
              >
                <ArrowLeft className="size-4" aria-hidden />
                {back.label}
              </Link>
            </div>
          ) : null}
          <UserRecordHeader user={user} surface={surface} />
        </div>
        <UserRecordTabList
          tabs={tabs}
          activeId={activeTabId}
          onSelect={onTabSelect}
          badges={badges}
        />
        <div className="pb-16">{children}</div>
      </div>
    </div>
  );
}
